import { ChangeDetectionStrategy, Component, HostListener, ElementRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngxs/store';
import { TribuNotification } from '@tribuu-api';
import { NotificationActions } from '@core/store/notifications/notifications.actions';
import { NotificationState } from '@core/store/notifications/notifications.state';
import { DateAgoPipe } from './date-ago.pipe';

@Component({
  selector: 'rec-notification-bell',
  standalone: true,
  imports: [CommonModule, RouterModule, DateAgoPipe],
  templateUrl: './notification-bell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationBellComponent {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly notifications = this.store.selectSignal(NotificationState.notifications);
  readonly unreadCount = this.store.selectSignal(NotificationState.unreadCount);
  readonly isOpen = this.store.selectSignal(NotificationState.isDropdownOpen);

  readonly maxVisible = 5;

  toggle(): void {
    const willOpen = !this.isOpen();
    this.store.dispatch(new NotificationActions.Toggle(willOpen));
    if (willOpen && this.unreadCount() > 0) {
      this.store.dispatch(new NotificationActions.ReadAll());
    }
  }

  close(): void {
    if (this.isOpen()) {
      this.store.dispatch(new NotificationActions.Toggle(false));
    }
  }

  goto(notification: TribuNotification): void {
    this.close();
    const target = notification.btnRedirect;
    if (!target) return;
    if (/^https?:\/\//i.test(target)) {
      window.open(target, '_blank', 'noopener,noreferrer');
      return;
    }
    this.router.navigateByUrl(target);
  }

  trackByUid(_index: number, notification: TribuNotification): string {
    return notification.uid;
  }

  visibleNotifications(): TribuNotification[] {
    return this.notifications().slice(0, this.maxVisible);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;
    if (this.isOpen() && !this.host.nativeElement.contains(target)) {
      this.close();
    }
  }
}
